import { keyframes } from "@emotion/react";
import { Container, Display, Flex, Heading, Stack, ArrowForwardIcon, Box, Loader } from "@eworkit/iris";
import React, { useRef } from "react";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useMedia } from "react-use";

import { MEDIA_SIZE_SM } from "@/core/media";
import { router } from "@/views/router";

import background from "../assets/background-pattern.svg";

interface DashboardOpenJobsButtonProps {
  count?: number;
}

export const DashboardOpenJobsButton: FC<DashboardOpenJobsButtonProps> = ({ count }) => {
  const { t } = useTranslation("session");
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const containerRef = useRef<HTMLDivElement>(null);

  const movingBackground = keyframes({
    from: { backgroundPositionX: "0" },
    to: { backgroundPositionX: `calc(100% - ${containerRef.current?.clientWidth}px)` },
  });

  const isLoading = count === -1; // Refactor this and use explicit async indicator

  return (
    <Container
      as={Link}
      sx={{
        display: "flex",
        flexDirection: "column",
        gridColumn: ["1 / 5", null, null, "auto"],
        backgroundColor: "vg900",
        backgroundImage: `url(${background})`,
        textDecoration: "none",
        backgroundSize: "auto 100%",
        backgroundRepeat: "repeat-x",
        borderRadius: "lg",
        transition: "200ms all",
        animation: `${movingBackground} 60s linear infinite`,
      }}
      to={router.get("job-requests").path}>
      {isLoading ? (
        <Flex flex={1} alignItems={"center"} flexDirection={"column"} justifyContent={"center"}>
          <Loader size={"lg"} />
        </Flex>
      ) : (
        <Container
          as={Box}
          flexGrow={2}
          ref={containerRef}
          sx={{
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0)",
            transition: "200ms all",
            "&:hover": {
              backgroundColor: "rgba(56, 114, 212, 0.1)",
              cursor: "pointer",
            },
            "&:hover .forward-arrow": {
              pr: "0",
              transition: "200ms all",
            },
          }}>
          <Container
            as={Flex}
            justifyContent="space-between"
            p={["sm", null, null, "md"]}
            sx={{ height: "100%" }}
            alignItems="center">
            <Stack gap={["xxs", null, null, "0"]}>
              {isSmallScreen ? (
                <Heading size="h-xl" color="n0">
                  {count}
                </Heading>
              ) : (
                <Display size="d-sm" color="n0">
                  {count}
                </Display>
              )}
              <Heading size="h-sm" color="n0">
                {t("DASHBOARD.OPEN_JOBS_WIDGET.HEADER")}
              </Heading>
            </Stack>
            <Box className="forward-arrow" alignSelf="end" pr="xs" pb="xxs" sx={{ transition: "200ms all" }}>
              <ArrowForwardIcon size="md" color="n0" />
            </Box>
          </Container>
        </Container>
      )}
    </Container>
  );
};
